//#region Imports

import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../shared/services/auth/auth.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthPayload } from '../../shared/services/auth/models/auth.payload';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ToastService } from "../../shared/components/toast/service/toast.service";
import { AuthForm } from '../../shared/models/interfaces/forms/auth-form.interface';
import { EMAIL_REGEX } from '../../shared/utils/variables';
import { ButtonComponent } from "../../shared/components/button/button.component";
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { SpinnerSizeEnum } from '../../shared/components/spinner/types/spinner-size.enum';
import { SpinnerColorEnum } from '../../shared/components/spinner/types/spinner-color.enum';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { tenantMock } from '../../shared/mocks/interfaces/tenant.mock';
import { Tenant } from '../../shared/models/interfaces/tenant.interface';
import { TenantThemeService } from '../../shared/services/tenant-theme/tenant-theme.service';
import { AnimatedBannerComponent } from '../../shared/components/animated-banner/animated-banner.component';
import { getCrudErrorMessage } from '../../shared/utils/functions';

//#endregion

@Component({
    selector: 'l-login',
    standalone: true,
    imports: [
      ReactiveFormsModule,
      RouterLink,
      IconComponent,
      ButtonComponent,
      SpinnerComponent,
      AnimatedBannerComponent
    ],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  //#region Constructor

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly tenantThemeService: TenantThemeService
  ) { 
    this.formGroup = this.formBuilder.group<AuthForm>({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      keepLoggedIn: new FormControl<boolean | null>(false),
    });
  }

  //#endregion

  //#region Public Properties

  public isLoading = false;

  public isLoadingLogin = false;

  public isHiddenPassword = true;

  public formGroup: FormGroup<AuthForm>;

  public hasSSO: boolean = false;

  public spinnerColors: typeof SpinnerColorEnum = SpinnerColorEnum;
  
  public spinnerSize: typeof SpinnerSizeEnum = SpinnerSizeEnum;
  
  public buttonTypes: typeof ButtonTypeEnum = ButtonTypeEnum;

  public buttonRadius: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public tenantData: Tenant | null = null;

  //#endregion

  //#region Getters

  public get isDisabled(): boolean {
    const { username, password } = this.formGroup.getRawValue();
    const hasEmail = !!username;
    const hasPassword = !!password;
    const isEmailValid = EMAIL_REGEX.test(username ?? '');
    const isPasswordValid = (password ?? '').length >= 6;

    return (!(hasEmail && hasPassword && isEmailValid && isPasswordValid)) || this.isLoading || this.isLoadingLogin;
  }

  //#endregion

  //#region Public Methods

  public async ngOnInit(): Promise<void> {
    await this.getTenantData();
  }

  public goToLogin(): void {
    return;
  }

  public async login(): Promise<void> {
    try {
      this.isLoadingLogin = true;
      const authPayload: AuthPayload = this.formGroup.getRawValue();
      await this.auth.login(authPayload);
    } catch (e: any) {
      this.toastService.error(getCrudErrorMessage(e, 'Erro ao autenticar.'));
    } finally {
      this.isLoadingLogin = false;
    }

    await this.router.navigate(['/dashboard']);
  }

  //#endregion

  //#region Private Methods

  private async getTenantData(): Promise<void> {
    // TODO: Implement tenant data retrieval
    try {
      this.isLoading = true;
      const cachedTenant = localStorage.getItem('tenant');
      const baseTenant = cachedTenant ? JSON.parse(cachedTenant) as Tenant : tenantMock[1];

      // TODO: Sem SSO: branding padrão Unico (sem logo/cor de tenant)
      this.tenantData = {
        ...baseTenant,
        primaryDomain: undefined,
        allowedDomains: undefined,
        logoUrl: 'assets/images/unico-logo.svg',
        primaryColor: undefined,
      };
      this.hasSSO = false;
      this.tenantThemeService.resetTheme();
    } catch (error: any) {
      this.toastService.error(getCrudErrorMessage(error));
    } finally {
      this.isLoading = false;
    }
  }

  //#endregion

}
